import { Close, ContactPage, Home, Menu, Quiz, Brightness4, Brightness7 } from '@mui/icons-material'
import {
  Box,
  Drawer,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  Theme
} from '@mui/material'
import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import ContactForm from '../components/ContactForm'
import FrequentQuestions from '../components/FrequentQuestions'
import { useUI } from '../hooks/useUI'
import '../i18n'
import Logo from './Logo'
import { useCompanySpaces } from '../queries/useCompanyQueries'

const HeaderStyled = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.common.white,
  borderBottom: `2px solid ${
    theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
  }`,
  position: 'fixed',
  width: '100%',
  top: 0,
  left: 0,
  zIndex: 1000,
  height: '70px',
  display: 'flex',
  alignItems: 'center'
}))

const HeaderContent = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: '0 auto',
  padding: '0 24px',
  [theme.breakpoints.up('lg')]: {
    padding: '0 48px'
  }
}))

const styleModal = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: (theme: Theme) =>
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.common.white,
  borderRadius: '10px',
  boxShadow: 24,
  p: 4,
  paddingBottom: '50px'
}

const ListItemStyled = styled(ListItem)(({ theme }) => ({
  borderBottom: `solid 1px ${theme.palette.mode === 'dark' ? '#ffffff20' : '#00000020'}`
}))

const Header = () => {
  const { i18n, t } = useTranslation()
  const { language, handleLangChange, handleRestart, setLanguage, darkMode, setDarkMode } = useUI()
  const { data: companyData } = useCompanySpaces()

  const [openDrawer, setOpenDrawer] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [openModalContact, setOpenModalContact] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('md'))

  const companyInfo = companyData?.body?.content[0]
  const languageCompany = companyInfo?.language

  const handleLanguageChange = useCallback(
    async (newLang: string) => {
      try {
        await i18n.changeLanguage(newLang)
        if (handleLangChange) {
          handleLangChange(newLang)
        }
        if (setLanguage) {
          setLanguage(newLang)
        }
      } catch (error) {
        console.error('Error changing language:', error)
      }
    },
    [i18n, handleLangChange, setLanguage]
  )

  console.log('companyData', companyData)

  useEffect(() => {
    if (isInitialLoad && companyInfo && languageCompany) {
      handleLanguageChange(languageCompany)
      setIsInitialLoad(false)
    }
  }, [companyInfo, languageCompany, handleLanguageChange, isInitialLoad])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const langParam = urlParams.get('lang')
    if (langParam && (langParam === 'en' || langParam === 'es')) {
      handleLanguageChange(langParam)
    }
  }, [handleLanguageChange])

  return (
    <>
      <HeaderStyled>
        <HeaderContent>
          <Stack
            direction="row"
            alignItems="space-between"
            sx={{
              position: 'relative',
              height: '100%'
            }}
          >
            {/* Logo - Izquierda */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
              {companyInfo?.urlLog && (
                <Logo
                  onClick={() => handleRestart && handleRestart()}
                  src={companyInfo.urlLog}
                  alt="logo"
                />
              )}
              {!companyInfo?.urlLog && <Box width={150} />}
            </Box>

            {/* Inicio - Centro */}
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                textAlign: 'center',
                cursor: 'pointer'
              }}
            >
              <Home
                fontSize="large"
                color="primary"
                onClick={() => handleRestart && handleRestart()}
              />
              <Typography
                fontWeight={700}
                onClick={() => handleRestart && handleRestart()}
                color={theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main'}
              >
                {t('home')}
              </Typography>
            </Box>

            {/* Controles - Derecha */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flex: 1, justifyContent: 'flex-end' }}
            >
              <IconButton onClick={() => setDarkMode && setDarkMode(!darkMode)} color="primary">
                {theme.palette.mode === 'dark' ? (
                  <Brightness7 fontSize="medium" />
                ) : (
                  <Brightness4 fontSize="medium" />
                )}
              </IconButton>

              {!isSm && (
                <FormControl size="small">
                  <Select
                    value={language}
                    autoWidth
                    sx={{
                      '& .MuiSelect-select': {
                        minWidth: {
                          xs: '30px',
                          md: '90px'
                        }
                      }
                    }}
                    onChange={(e) => handleLanguageChange(e.target.value as string)}
                  >
                    <MenuItem value="en">{t('english')}</MenuItem>
                    <MenuItem value="es">{t('spanish')}</MenuItem>
                  </Select>
                </FormControl>
              )}

              <IconButton color="primary" onClick={() => setOpenDrawer(true)} edge="end">
                <Menu fontSize="large" />
              </IconButton>
            </Stack>
          </Stack>
        </HeaderContent>
      </HeaderStyled>

      {/* Drawer y Modales */}
      <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)}>
        <Box sx={{ width: 350, bgcolor: 'background.paper' }}>
          <List>
            <ListItemStyled sx={{ justifyContent: 'flex-end' }}>
              <IconButton onClick={() => setOpenDrawer(false)}>
                <Close fontSize="large" color="primary" />
              </IconButton>
            </ListItemStyled>
            <ListItemStyled>
              <ListItemButton onClick={() => setOpenModalContact(true)}>
                <ListItemIcon>
                  <ContactPage color="primary" />
                </ListItemIcon>
                <ListItemText primary={t('contact')} />
              </ListItemButton>
            </ListItemStyled>
            <ListItemStyled>
              <ListItemButton onClick={() => setOpenModal(true)}>
                <ListItemIcon>
                  <Quiz color="primary" />
                </ListItemIcon>
                <ListItemText primary={t('faq')} />
              </ListItemButton>
            </ListItemStyled>
            {isSm && (
              <ListItemStyled sx={{ borderBottom: 0 }}>
                <FormControl sx={{ m: 1, minWidth: 60 }}>
                  <Select
                    value={language}
                    autoWidth
                    sx={{
                      '& .MuiSelect-select': {
                        minWidth: {
                          xs: '30px',
                          md: '90px'
                        }
                      }
                    }}
                    onChange={(e) => handleLanguageChange(e.target.value as string)}
                  >
                    <MenuItem value="en">En</MenuItem>
                    <MenuItem value="es">Es</MenuItem>
                  </Select>
                </FormControl>
              </ListItemStyled>
            )}
          </List>
        </Box>
      </Drawer>

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            ...styleModal,
            width: { xs: '90%', md: '800px' },
            height: { xs: '90%', md: 'auto' },
            overflowY: { xs: 'scroll', md: 'hidden' }
          }}
        >
          <Box display="flex" justifyContent="flex-end" alignItems="center" marginBottom={2}>
            <IconButton onClick={() => setOpenModal(false)}>
              <Close />
            </IconButton>
          </Box>
          <FrequentQuestions />
        </Box>
      </Modal>

      <Modal
        open={openModalContact}
        onClose={() => setOpenModalContact(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...styleModal, width: { xs: '80%', md: '600px' } }}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" marginBottom={2}>
            <IconButton onClick={() => setOpenModalContact(false)}>
              <Close />
            </IconButton>
          </Box>
          <ContactForm />
        </Box>
      </Modal>
    </>
  )
}

export default Header
